/**
 * SOFSEK — Starter / Leaver / Force-Add Intake Queue (§43). Starter/leaver
 * requests are created PENDING here (full APR approval-chain routing — mirroring
 * CSM's new-user registration pattern — is a configuration step on top of this
 * request model, wired when APR policy for SOFSEK intake is set up). Force-add
 * is a permission-gated, audited bypass: creates the request already APPLIED,
 * recording actor + reason.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import { routedInsert } from '../../lib/routed_writes';
import { getForensicActorId } from '../../types/auth';
import { hasAccess, type AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { EmptyState } from '../../components/common/EmptyState';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { useFormatDateTime } from '../../lib/format_date';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { RefreshCw, Search, ShieldAlert } from 'lucide-react';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';

const PAGE_CODE = 'SFK.INTAKE_REQUEST.LIST';
const inputCls = 'mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-sm';
const labelCls = 'mb-1 block text-csm-sm font-medium text-csm-ink-soft';
const dangerBtnCls = 'h-7 rounded border border-csm-danger bg-csm-danger px-2 text-csm-sm text-white hover:opacity-90';

interface CodeOpt { code: string; name: string; }
interface RequestRow {
  request_id: number;
  app_user_id: number | null;
  application_id: number;
  intake_type_code: string;
  intake_status_code: string;
  is_force_add: 'Y' | 'N';
  intake_reason_code: string | null;
  created_at: string;
}

const GRID_COLS: GridColumnDef[] = [
  { key: 'application', header: 'Application', defaultWidth: 180 },
  { key: 'intake_type', header: 'Type', defaultWidth: 130 },
  { key: 'status', header: 'Status', defaultWidth: 130 },
  { key: 'force_add', header: 'Force-Add', defaultWidth: 100 },
  { key: 'reason', header: 'Reason', defaultWidth: 200 },
  { key: 'created_at', header: 'Created', defaultWidth: 170 },
];

function tlName(list: unknown, fallback: string): string {
  const arr = (list ?? []) as { locale_code: string; display_text: string }[];
  return arr.find(x => x.locale_code === 'EN_US')?.display_text ?? fallback;
}

function IntakeInner({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [rows, setRows] = useState<RequestRow[]>([]);
  const [apps, setApps] = useState<CodeOpt[]>([]);
  const [types, setTypes] = useState<CodeOpt[]>([]);
  const [statuses, setStatuses] = useState<CodeOpt[]>([]);
  const [reasons, setReasons] = useState<CodeOpt[]>([]);
  const [filter, setFilter] = useState('');
  const [typeFacet, setTypeFacet] = useState<string[]>([]);
  const [statusFacet, setStatusFacet] = useState<string[]>([]);
  const [creatingForceAdd, setCreatingForceAdd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [reqRes, appRes, typeRes, statusRes, reasonRes] = await Promise.all([
        apiDb.from('sfk_intake_request').select('request_id, app_user_id, application_id, intake_type_code, intake_status_code, is_force_add, intake_reason_code, created_at').order('created_at', { ascending: false }),
        apiDb.from('csm_application_master').select('application_id, csm_application_master_tl ( locale_code, display_text )'),
        apiDb.from('sfk_intake_type').select('intake_type_code, sfk_intake_type_tl ( locale_code, display_text )'),
        apiDb.from('sfk_intake_status').select('intake_status_code, sfk_intake_status_tl ( locale_code, display_text )'),
        apiDb.from('sfk_intake_reason').select('intake_reason_code, sfk_intake_reason_tl ( locale_code, display_text )'),
      ]);
      if (reqRes.error) throw reqRes.error;
      if (appRes.error) throw appRes.error;
      if (typeRes.error) throw typeRes.error;
      if (statusRes.error) throw statusRes.error;
      if (reasonRes.error) throw reasonRes.error;
      setRows((reqRes.data ?? []) as unknown as RequestRow[]);
      setApps(((appRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: String(r.application_id), name: tlName(r.csm_application_master_tl, String(r.application_id)) })));
      setTypes(((typeRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: r.intake_type_code as string, name: tlName(r.sfk_intake_type_tl, r.intake_type_code as string) })));
      setStatuses(((statusRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: r.intake_status_code as string, name: tlName(r.sfk_intake_status_tl, r.intake_status_code as string) })));
      setReasons(((reasonRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: r.intake_reason_code as string, name: tlName(r.sfk_intake_reason_tl, r.intake_reason_code as string) })));
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

   
  useEffect(() => { void reload(); }, []);

  const appName = (id: number) => apps.find(a => a.code === String(id))?.name ?? String(id);
  const resolveNameForType = (code: string) => types.find(x => x.code === code)?.name ?? code;
  const resolveNameForStatus = (code: string) => statuses.find(x => x.code === code)?.name ?? code;
  const resolveNameForReason = (code: string | null) => (code ? reasons.find(x => x.code === code)?.name ?? code : '—');

  const detail = useExpandedRows<number>();
  // §6/§17: csm_role_page_access decides which write affordances this user is
  // offered. The server refuses the write regardless (RLS, via the Common
  // Routine) — this stops the page offering an action that can only error.
  const canAdd    = hasAccess(session, PAGE_CODE, 'add');
  // §17.B13 — the resolved references behind each intake request.
  const buildRelatedGroups = (r: RequestRow): RelatedChildGroup[] => [
    { key: 'detail', title: t('sfk.intake.related.title', 'Request detail'), items: [
      { id: 'application', label: `${t('sfk.intake.col.application', 'Application')}: ${appName(r.application_id)}`, title: String(r.application_id) },
      { id: 'type', label: `${t('sfk.intake.col.type', 'Type')}: ${resolveNameForType(r.intake_type_code)}` },
      { id: 'status', label: `${t('sfk.intake.col.status', 'Status')}: ${resolveNameForStatus(r.intake_status_code)}` },
      ...(r.intake_reason_code ? [{ id: 'reason', label: `${t('sfk.intake.col.reason', 'Reason')}: ${resolveNameForReason(r.intake_reason_code)}` }] : []),
    ] },
  ];

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter(r =>
      (typeFacet.length === 0 || typeFacet.includes(r.intake_type_code)) &&
      (statusFacet.length === 0 || statusFacet.includes(r.intake_status_code)) &&
      (!q || appName(r.application_id).toLowerCase().includes(q))
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, typeFacet, statusFacet, filter, apps]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('sfk.intake.title', 'Starter / Leaver / Force-Add Intake')}
          description={t('sfk.intake.subtitle', 'Starter/leaver requests and permission-gated, audited force-add bypasses.')}
          actions={
            <>
              {canAdd && (<button onClick={() => setCreatingForceAdd(true)} className="flex items-center gap-1.5 rounded-csm-control border border-csm-danger bg-csm-danger px-2.5 py-1.5 text-csm-sm text-white hover:opacity-90"><ShieldAlert className="h-3.5 w-3.5" /> {t('sfk.intake.force_add', 'Force-Add')}</button>)}
              <button onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}</button>
            </>
          }
        />
        <StatStrip stats={[{ value: rows.length, label: t('common.total', 'Total') }, { value: filtered.length, label: t('common.shown', 'Shown') }]} />
        {error && <div className="rounded-csm-card border border-csm-danger-border bg-csm-danger-soft px-3 py-1.5 text-csm-xs text-csm-danger">{error}</div>}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('common.filter', 'Filter…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
            <FacetFilter label={t('sfk.intake.facet.type', 'Type')} options={types.map(x => ({ value: x.code, label: x.name }))} selected={typeFacet} onChange={setTypeFacet} />
            <FacetFilter label={t('sfk.intake.facet.status', 'Status')} options={statuses.map(x => ({ value: x.code, label: x.name }))} selected={statusFacet} onChange={setStatusFacet} />
            <ClearFiltersButton active={filter.trim() !== '' || typeFacet.length > 0 || statusFacet.length > 0} onClear={() => { setFilter(''); setTypeFacet([]); setStatusFacet([]); }} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading', 'Loading…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="flex-1 overflow-auto">
            <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="border-b border-csm-border px-1 py-1.5 text-left" style={{ width: '1%' }} aria-hidden>&nbsp;</th>
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.request_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle">
                      <RelatedExpandButton open={detail.isOpen(r.request_id)} hasChildren onToggle={() => detail.toggle(r.request_id)}
                        expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'application' && appName(r.application_id)}
                        {col.key === 'intake_type' && resolveNameForType(r.intake_type_code)}
                        {col.key === 'status' && resolveNameForStatus(r.intake_status_code)}
                        {col.key === 'force_add' && (r.is_force_add === 'Y' ? t('common.boolean.yes', 'Yes') : t('common.boolean.no', 'No'))}
                        {col.key === 'reason' && resolveNameForReason(r.intake_reason_code)}
                        {col.key === 'created_at' && formatDateTime(r.created_at)}
                      </td>
                    ))}
                  </tr>
                  {detail.isOpen(r.request_id) && (
                    <tr>
                      <td colSpan={visibleCols.length + 1} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-2">
                        <RelatedChildrenPanel groups={buildRelatedGroups(r)} emptyLabel={t('common.related.none', 'No related records')} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 1} className="py-12 text-center"><EmptyState title={t('common.no_records', 'No records')} /></td></tr>
                )}
              </tbody>
            </table>
          )}
            </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>

      {creatingForceAdd && (
        <ForceAddModal session={session} apps={apps} reasons={reasons}
          onClose={() => setCreatingForceAdd(false)}
          onSaved={() => { setCreatingForceAdd(false); void reload(); }} />
      )}
    </div>
  );
}

function ForceAddModal({ session, apps, reasons, onClose, onSaved }: {
  session: AuthSession; apps: CodeOpt[]; reasons: CodeOpt[]; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [applicationId, setApplicationId] = useState(apps[0]?.code ?? '');
  const [reasonCode, setReasonCode] = useState(reasons[0]?.code ?? '');
  const [requestedAccess, setRequestedAccess] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (!applicationId) throw new Error(t('sfk.intake.err.app_required', 'Application is required.'));
      if (!reasonCode) throw new Error(t('sfk.intake.err.reason_required', 'A reason code is required.'));
      const { error: e } = await routedInsert(session, 'sfk_intake_request', {
        application_id: Number(applicationId),
        intake_type_code: 'FORCE_ADD',
        intake_status_code: 'APPLIED',
        is_force_add: 'Y',
        intake_reason_code: reasonCode,
        requested_access: requestedAccess.trim() || null,
        actor_user_id: getForensicActorId(session),
      });
      if (e) throw new Error(e.message);
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <Modal
      title={t('sfk.intake.force_add_title', 'Force-Add Access (Bypass)')}
      onClose={onClose}
      width="md"
      headerAction={<PersonalizationToggle />}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          <button onClick={() => void save()} disabled={busy} className={dangerBtnCls + ' disabled:opacity-60'}>{busy ? t('common.saving', 'Saving…') : t('sfk.intake.force_add_confirm', 'Force-Add')}</button>
        </>
      }
    >
      <p className="mb-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-xs text-csm-danger">
        {t('sfk.intake.force_add_warning', 'This bypasses the governed starter/leaver approval chain. The actor and reason are permanently recorded.')}
      </p>
      <PersonalizedField componentKey="modal" fieldKey="application_id" label={t('sfk.intake.application', 'Application')}>
        <label className={labelCls}>{t('sfk.intake.application', 'Application')}</label>
        <select value={applicationId} onChange={e => setApplicationId(e.target.value)} className={`${inputCls} bg-white`}>
          {apps.map(a => <option key={a.code} value={a.code}>{a.name}</option>)}
        </select>
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="intake_reason_code" label={t('sfk.intake.reason', 'Reason')}>
        <label className={labelCls}>{t('sfk.intake.reason', 'Reason')}</label>
        <select value={reasonCode} onChange={e => setReasonCode(e.target.value)} className={`${inputCls} bg-white`}>
          {reasons.map(r => <option key={r.code} value={r.code}>{r.name}</option>)}
        </select>
      </PersonalizedField>
      <PersonalizedField componentKey="modal" fieldKey="requested_access" label={t('sfk.intake.requested_access', 'Access Notes')}>
        <label className={labelCls}>{t('sfk.intake.requested_access', 'Access Notes')}</label>
        <textarea value={requestedAccess} onChange={e => setRequestedAccess(e.target.value)} rows={3} className="mb-3 w-full resize-y rounded border border-csm-border px-2 py-1 text-csm-sm" />
      </PersonalizedField>
      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

export function SfkIntakeRequestPage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <IntakeInner session={session} />
    </PersonalizationProvider>
  );
}
